// ============================================================================
// HILO — la conversación. Columna central del inbox (C7-D2).
// Lo que tiene que quedar claro de un vistazo: QUIÉN escribió (cliente / bot /
// asesor), SI el mensaje salió de verdad (estado de entrega) y CUÁNTO queda de
// la ventana de 24 h, porque fuera de ella WhatsApp no deja escribir libre.
// ============================================================================
import { memo, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import {
  AlertTriangle, ArrowLeft, Bot, Check, CheckCheck, Clock, EyeOff, FileText, Info, Mic, Paperclip, Send, Trash2, User, Zap,
} from "lucide-react";
import { Avatar, Aviso, Boton, Chip, Vacio } from "./piezas.tsx";
import { formatoWhatsApp } from "./formatoWhatsApp.tsx";
import { explicarError, FORMATOS_ADJUNTO } from "./datos.ts";
import { COP_POR_USD } from "./Metricas.tsx";
import { esContactoIg, identificadorVisible } from "./canal_contacto.ts";
import {
  laTieneAlguien, nadieLaHaTomado, nombreMostrado, restanteVentana, ventanaAbierta,
  type Conversacion, type Mensaje, type RespuestaRapida,
} from "./tipos.ts";

const DIA_LARGO: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long" };

function Entrega({ estado }: { estado: Mensaje["entrega"] }) {
  if (!estado) return null;
  const comun = "inline-block align-[-2px]";
  if (estado === "encolado") return <Clock size={13} className={`${comun} opacity-50`} aria-label="En cola" />;
  if (estado === "enviado") return <Check size={13} className={`${comun} opacity-60`} aria-label="Enviado" />;
  if (estado === "entregado") return <CheckCheck size={13} className={`${comun} opacity-60`} aria-label="Entregado" />;
  if (estado === "leido") return <CheckCheck size={13} className={`${comun} text-marca`} aria-label="Leído" />;
  // ⭐ T-CAL-22 · I-183 · la nota que el cliente reprodujo. Sin esta rama, el estado nuevo
  //   caía al triángulo de abajo y TODA nota escuchada se pintaba «Falló el envío».
  if (estado === "escuchado") return <CheckCheck size={13} className={`${comun} text-marca`} aria-label="Escuchado" />;
  return <AlertTriangle size={13} className={`${comun} text-peligro`} aria-label="Falló el envío" />;
}

/**
 * Lo que mandó el CLIENTE: su nota de voz o su foto (C2-D4).
 *
 * Por qué existe: hasta el 1-ago-2026 estos mensajes salían como una burbuja
 * VACÍA. El archivo estaba guardado y el bot ya lo había entendido, pero el
 * asesor abría el chat y no veía nada. Es la misma clase de fallo del bug de
 * `media_url`: todo funciona menos la pantalla, y ninguna prueba de backend lo ve.
 *
 * ⚠️ La URL se pide al abrir la burbuja y CADUCA: el bucket es privado (fotos de
 * la casa del cliente, su voz). No se puede guardar una URL fija en la base.
 */
function MediaDelCliente({ m, firmar }: {
  m: Mensaje;
  firmar?: (ruta: string) => Promise<string | null>;
}) {
  const media = m.media;
  const [url, setUrl] = useState<string | null>(null);
  const [fallo, setFallo] = useState(false);

  useEffect(() => {
    let vivo = true;
    // ⭐ T-LEY-10e-B · I-370 · lo suprimido (R-LEY-10) NO se pide firmar: el original
    //    ya no está y pedir la firma sería volver a buscarlo.
    if (!media?.storage_path || !firmar || media.estado === "suprimido") return;
    firmar(media.storage_path)
      .then((u) => { if (vivo) { setUrl(u); setFallo(!u); } })
      .catch(() => { if (vivo) setFallo(true); });
    return () => { vivo = false; };
  }, [media?.storage_path, media?.estado, firmar]);

  if (!media) return null;
  const esAudio = m.tipo === "audio";
  const esImagen = m.tipo === "imagen";
  const dudosa = typeof media.confianza === "number" && media.confianza < 0.55;

  return (
    <div className="mb-1">
      {/* El archivo, cuando ya se pudo firmar */}
      {/* ⚠️ `preload="metadata"`, NO `none`. Con `none` el navegador no baja ni la
          cabecera del archivo, así que el reproductor enseña «0:00 / 0:00» hasta
          que alguien le da play: el asesor no puede saber si la nota dura 3
          segundos o 3 minutos antes de oírla. `metadata` baja solo la cabecera,
          que es lo que costaba `none` evitar (una conversación con 20 audios NO
          se descarga entera al abrirla).
          🔴 LO QUE ESTO **NO** RESUELVE, y hay que medirlo en el navegador antes
          de darlo por bueno: un `.m4a` producido por `MediaRecorder` suele ser MP4
          FRAGMENTADO y su cabecera puede no traer duración — ahí el navegador
          reporta `Infinity` y el reloj sigue mintiendo, ahora de otra forma. La
          solución robusta si eso pasa es guardar la duración al subir (en
          `mensajes_media` o en `cuerpo`) y pintarla nosotros, no depender del
          contenedor. NO verificado en pantalla por el constructor. */}
      {url && esAudio && (
        <audio controls preload="metadata" src={url} className="w-[260px] max-w-full h-9 mb-1">
          Tu navegador no puede reproducir este audio.
        </audio>
      )}
      {url && esImagen && (
        <img src={url} alt="Foto que envió el cliente" loading="lazy"
          className="rounded-lg mb-1 max-h-64 w-auto object-cover" />
      )}

      {/* Estados en los que NO hay archivo que mostrar. Se DICEN, no se callan:
          una burbuja vacía deja al asesor sin saber si el cliente mandó algo. */}
      {!url && !fallo && media.estado === "pendiente" && (
        <Chip tono="neutro"><Info size={11} /> Descargando {esAudio ? "la nota de voz" : "el archivo"}…</Chip>
      )}
      {media.estado === "suprimido" && (
        <Chip tono="neutro"><Info size={11} /> Archivo suprimido</Chip>
      )}
      {media.estado === "fallido" && (
        <Chip tono="alerta">
          <AlertTriangle size={11} /> No se pudo guardar {esAudio ? "la nota de voz" : "el archivo"}
        </Chip>
      )}
      {fallo && media.estado !== "fallido" && (
        <Chip tono="neutro"><AlertTriangle size={11} /> No se pudo abrir el archivo</Chip>
      )}

      {/* La transcripción. Va SIEMPRE que exista, incluso con el audio a la
          vista: es exactamente lo que leyó el bot para responder, así que si
          contestó raro, aquí se ve por qué. */}
      {media.transcripcion && media.estado !== "suprimido" && (
        <div className={`mt-1 rounded-lg px-2 py-1.5 border ${
          dudosa ? "border-alerta/40 bg-alerta/10" : "border-borde-suave bg-tenue"}`}>
          <div className="flex items-center gap-1 mb-0.5">
            <Mic size={10} className="opacity-60" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-tinta-2">
              {dudosa ? "Transcripción poco clara" : "Transcripción"}
            </span>
          </div>
          <p className="text-[13.5px] leading-[1.4] italic whitespace-pre-wrap break-words">
            {media.transcripcion}
          </p>
        </div>
      )}
    </div>
  );
}


/**
 * Lo que adjuntó el ASESOR (C2-D6): su foto, su nota de voz, un PDF.
 *
 * Vive en el mismo bucket privado que lo del cliente, así que también necesita
 * URL firmada. Un documento se muestra como una tarjeta con su nombre: es lo que
 * el cliente va a ver en su teléfono, y el asesor tiene que poder comprobarlo.
 */
function AdjuntoSaliente({ m, firmar }: {
  m: Mensaje; firmar?: (ruta: string) => Promise<string | null>;
}) {
  const ruta = String(m.cuerpo?.storage_path ?? "");
  const nombre = String(m.cuerpo?.nombre_archivo ?? "archivo");
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    let vivo = true;
    if (!ruta || !firmar) return;
    firmar(ruta).then((u) => { if (vivo) setUrl(u); }).catch(() => {});
    return () => { vivo = false; };
  }, [ruta, firmar]);

  // ⛔ R-ADJ-01 · EL AVISO DE QUE NO SALIÓ VIVE AQUÍ, no en `MediaDelCliente`.
  // Desde la 0045 un saliente con archivo SÍ tiene fila en `mensajes_media`, y el
  // worker la deja en `fallido` cuando la entrega muere. Si el aviso se quedara
  // solo en el componente del cliente, el asesor volvería a ver su adjunto como si
  // hubiera salido — que es medio defecto del 31-ago.
  // ⚠️ Y el texto NO es «no se pudo guardar»: el archivo está guardado (lo subió el
  // panel antes de crear el mensaje). Lo que falló es el ENVÍO, y de eso depende lo
  // que el asesor haga después: reintentar el envío, no volver a adjuntar.
  const fallo = m.media?.estado === "fallido";
  const aviso = fallo
    ? (
      <Chip tono="alerta">
        <AlertTriangle size={11} /> No se pudo enviar {m.tipo === "audio" ? "la nota de voz" : "el archivo"}
      </Chip>
    )
    : null;

  if (m.tipo === "imagen") {
    return (
      <>
        {url
          ? <img src={url} alt={nombre} loading="lazy" className="rounded-lg mb-1 max-h-64 w-auto object-cover" />
          : <Chip tono="neutro">Cargando la imagen…</Chip>}
        {aviso}
      </>
    );
  }
  if (m.tipo === "audio") {
    // ⚠️ `w-[260px] max-w-full`, NUNCA `w-full`: la burbuja se ajusta a su
    // contenido, así que un `w-full` aquí es circular —el audio pide el ancho
    // del padre y el padre el del hijo— y el reproductor sale APLASTADO, con los
    // controles cortados. Se vio el 1-ago-2026, ya de noche mandando la primera nota de voz
    // de verdad desde el panel. En la burbuja del CLIENTE no pasaba porque la
    // transcripción le daba ancho por debajo.
    return (
      <>
        {url
          // `preload="metadata"` por lo mismo que en la burbuja del cliente (ver
          // el comentario largo ahí): con `none` el reloj dice «0:00 / 0:00».
          ? <audio controls preload="metadata" src={url} className="w-[260px] max-w-full h-9 mb-1" />
          : <Chip tono="neutro">Cargando la nota de voz…</Chip>}
        {aviso}
      </>
    );
  }
  // Documento (y video): tarjeta con el nombre REAL, que es lo que ve el cliente.
  return (
    <>
      <a href={url ?? undefined} target="_blank" rel="noreferrer"
        className={`flex items-center gap-2 rounded-lg border border-borde-suave bg-tenue px-2.5 py-2 mb-1
          ${url ? "hover:border-marca/40" : "opacity-60 pointer-events-none"}`}>
        <FileText size={16} className="shrink-0 opacity-70" />
        <span className="text-[13px] break-all">{nombre}</span>
      </a>
      {aviso}
    </>
  );
}

/**
 * ⭐ T-CAL-12 · I-173 · el costo de UNA nota de voz del bot, en pesos
 * aproximados, con la misma tasa de `Metricas.tsx` («≈ $20 COP»). Por debajo
 * de un peso dice «< $1 COP»: redondear a «≈ $0» haría creer que fue gratis.
 * `null` si el dato no está o no es un número usable — y entonces no se pinta.
 */
function costoVozEnPesos(usd: unknown): string | null {
  if (typeof usd !== "number" || !Number.isFinite(usd) || usd < 0) return null;
  const cop = usd * COP_POR_USD;
  if (cop < 1) return "< $1 COP";
  return `≈ $${Math.round(cop).toLocaleString("es-CO")} COP`;
}

/**
 * ⭐ T-CAL-12 · I-173 · LO QUE DIJO EL BOT EN SU NOTA DE VOZ, debajo del reproductor.
 * El asesor nunca tiene que oír la nota para saber qué se le dijo al cliente: la
 * charla (`cuerpo.texto`, la que el worker guardó TAL CUAL al hablar) va con el
 * mismo estilo de la transcripción del cliente, rotulada, y el costo de la voz
 * (`cuerpo.costo_usd`, lo que cobró ElevenLabs) en pesos. Si falta uno, ese
 * bloque simplemente no sale.
 * ⚠️ Es SOLO de la nota del bot: la del asesor no lleva texto ni costo.
 */
function LoQueDijoElBot({ m }: { m: Mensaje }) {
  const texto = typeof m.cuerpo?.texto === "string" && m.cuerpo.texto.trim() ? m.cuerpo.texto : null;
  const costo = costoVozEnPesos(m.cuerpo?.costo_usd);
  if (!texto && !costo) return null;
  return (
    <div data-nota-del-bot className="mt-1 rounded-lg px-2 py-1.5 border border-borde-suave bg-tenue">
      {texto && (
        <>
          <div className="flex items-center gap-1 mb-0.5">
            <Mic size={10} className="opacity-60" />
            <span className="text-[10px] font-semibold uppercase tracking-wider text-tinta-2">
              Lo que dijo el bot
            </span>
          </div>
          <p className="text-[13.5px] leading-[1.4] italic whitespace-pre-wrap break-words">
            {formatoWhatsApp(texto)}
          </p>
        </>
      )}
      {costo && (
        <div data-costo-voz className={`text-[10.5px] text-tinta-2 cifra ${texto ? "mt-1" : ""}`}
          title={`US$${Number(m.cuerpo?.costo_usd).toFixed(4)} · a $${COP_POR_USD.toLocaleString("es-CO")}/USD`}>
          Costo de la voz {costo}
        </div>
      )}
    </div>
  );
}

/** ⭐ T-CAL-22 · el momento de la voz, como lo lee el dueño (lista cerrada de la 0085). */
const MOMENTO_VOZ: Record<string, string> = { recomendacion: "Recomendación", compra: "Compra" };
/** 24 h: la misma ventana con que `voz_resultados` (0087) cuenta «respondió». */
const DIA_MS = 24 * 3_600_000;

/**
 * ⭐ T-CAL-22 · ¿el CLIENTE escribió después de esta nota y dentro de las 24 h? Solo mira
 * este hilo (los mensajes vienen en orden). Sin respuesta ⇒ false, y la línea omite la
 * parte (nunca dice «no»: puede que aún no pasen las 24 h o que respondiera en otro chat).
 */
export function respondioALaNota(mensajes: Mensaje[], i: number): boolean {
  const nota = mensajes[i];
  if (!nota) return false;
  const t0 = new Date(nota.creado_en).getTime();
  for (let j = i + 1; j < mensajes.length; j++) {
    const m = mensajes[j];
    if (m.direccion !== "in" || m.autor !== "cliente") continue;
    const t = new Date(m.creado_en).getTime();
    if (t > t0 && t - t0 <= DIA_MS) return true;
  }
  return false;
}

/**
 * ⭐ T-CAL-22 · I-183 · EL RASTRO DE LA NOTA DEL BOT, en una línea discreta bajo lo que dijo:
 * «Recomendación · escuchado ✓ · respondió ✓». Cada parte sale SOLO si hay dato (momento en
 * el `cuerpo`, `escuchado` en la entrega, un entrante del cliente en 24 h); sin ninguna, no
 * hay línea. Nunca «no escuchado» ni «no respondió»: no saberlo no es un no.
 */
function RastroDeLaNota({ m, respondio }: { m: Mensaje; respondio: boolean }) {
  const partes: string[] = [];
  const momento = typeof m.cuerpo?.momento === "string" ? MOMENTO_VOZ[m.cuerpo.momento] : undefined;
  if (momento) partes.push(momento);
  if (m.entrega === "escuchado") partes.push("escuchado ✓");
  if (respondio) partes.push("respondió ✓");
  if (partes.length === 0) return null;
  return (
    <div data-rastro-nota className="mt-1 text-[11px] text-tinta-2 leading-snug break-words">
      {partes.join(" · ")}
    </div>
  );
}

function TarjetaLlamada({ m }: { m: Mensaje }) {
  const c: any = m.cuerpo ?? {};
  const seg = Number(c.duracion_s) || 0;
  const min = Math.max(1, Math.ceil(seg / 60));
  const turnos: Array<{ rol: string; texto: string; cortado?: boolean }> = Array.isArray(c.turnos) ? c.turnos : [];
  return (
    <div data-llamada className="flex justify-center px-3 sm:px-5 my-2">
      <div className="w-full max-w-[92%] sm:max-w-[70%] rounded-2xl border border-borde-suave bg-superficie px-3 py-2">
        <div className="text-[12px] font-semibold text-acento uppercase tracking-wider">📞 Llamada · {min} min</div>
        <div data-llamada-resumen className="text-[13.5px] leading-snug whitespace-pre-line break-words mt-1">
          {String(c.resumen ?? "Sin resumen")}
        </div>
        {turnos.length > 0 && (
          <details className="mt-1.5">
            <summary className="cursor-pointer text-[12px] text-marca select-none">
              Ver transcripción ({turnos.length} {turnos.length === 1 ? "turno" : "turnos"}){c.recortado ? " · recortada al inicio" : ""}
            </summary>
            <ol className="mt-1.5 space-y-1">
              {turnos.map((t, i) => (
                <li key={i} data-turno={t.rol} className="text-[13px] leading-snug break-words">
                  <span className="font-semibold">{t.rol === "cliente" ? "Cliente" : "Oscar"}:</span>{" "}
                  {String(t.texto ?? "")}{t.cortado ? <span className="opacity-60"> (lo interrumpieron)</span> : null}
                </li>
              ))}
            </ol>
          </details>
        )}
      </div>
    </div>
  );
}

function Burbuja({ m, firmarMedia, respondio = false, nuevo = false }: {
  m: Mensaje; firmarMedia?: (ruta: string) => Promise<string | null>;
  /** T-CAL-22: solo lo usa la nota de voz del bot (`respondioALaNota`). */
  respondio?: boolean;
  /** Llegó con el chat ya abierto: solo ESA burbuja entra con movimiento. */
  nuevo?: boolean;
}) {
  // ⭐ T-HIB-1 · I-86 · LA INSTRUCCIÓN AL BOT ES UNA NOTA INTERNA, Y SE VE ASÍ.
  // Ni burbuja del asesor (el asesor creería que el cliente la leyó) ni la
  // píldora gris del pase (se perdería entre los eventos). Borde punteado, a la
  // derecha como lo del equipo, y el rótulo lo dice: el cliente no la ve.
  if (m.tipo === "sistema" && m.cuerpo?.evento === "instruccion_al_bot") {
    return (
      <div className="flex justify-end px-3 sm:px-5">
        <div data-nota-interna="instruccion_al_bot"
          className="max-w-[85%] sm:max-w-[68%] md:max-w-[60%] rounded-2xl border border-dashed border-marca/50
            bg-marca-suave/40 px-3 py-2 my-1">
          <div className="flex items-center gap-1 mb-0.5">
            <EyeOff size={11} className="text-acento" />
            <span className="text-[10.5px] font-semibold text-acento uppercase tracking-wider">
              Instrucción al bot · el cliente no la ve
            </span>
          </div>
          <div className="text-[14px] leading-snug whitespace-pre-wrap break-words italic">
            {String(m.cuerpo?.texto ?? "")}
          </div>
        </div>
      </div>
    );
  }
  // ★ T-CAL-15 (llamada escrita) · I-253 · LA LLAMADA: tarjeta «📞 Llamada · N min» con el resumen a la
  //   vista y la transcripción desplegable (Cliente / Oscar). Va ANTES de la píldora: su autor es
  //   'sistema' y sin esta rama caería ahí como «Evento».
  if (m.tipo === "llamada") return <TarjetaLlamada m={m} />;
  // Mensaje de sistema (transferencias, cierres): píldora centrada, no burbuja.
  // ⭐ T-CAL-B5 · I-118 · la píldora PARTE en líneas (`envolver`): «Pasó a un
  // asesor · » + el resumen del pase (≤ 80) son ~99 letras a 11 px ≈ 570 px, y el
  // celular mide 375 — con `whitespace-nowrap` se salía por los lados (revisor
  // T-CAL-7 r2 · R2-4). `px-3` deja aire con el borde; el icono no se encoge.
  if (m.tipo === "sistema" || m.autor === "sistema") {
    const evento = String(m.cuerpo?.evento ?? "");
    const texto = evento === "transferido_humano"
      ? `Pasó a un asesor${m.cuerpo?.motivo ? ` · ${m.cuerpo.motivo}` : ""}`
      : String(m.cuerpo?.texto ?? (evento || "Evento"));
    return (
      <div data-pildora-sistema className="flex justify-center my-2 px-3">
        <Chip tono="neutro" envolver><Info size={11} className="shrink-0" /> {texto}</Chip>
      </div>
    );
  }

  const entrante = m.direccion === "in";
  const esBot = m.autor === "bot";
  const fondo = entrante
    ? "bg-[var(--color-burbuja-in)] border border-borde-suave"
    : esBot ? "bg-[var(--color-burbuja-bot)]" : "bg-[var(--color-burbuja-out)]";
  const cola = entrante ? "var(--color-burbuja-in)" : esBot ? "var(--color-burbuja-bot)" : "var(--color-burbuja-out)";
  const url = typeof m.cuerpo?.media_url === "string" ? m.cuerpo.media_url : null;
  // ⭐ T-BOT-2. Un toque de botón (T-BOT-1 lo guarda con `cuerpo.boton`) y la
  // plantilla que lo originó (I-70) se DICEN, en el mismo rótulo discreto que
  // «Bot»/«Asesor»: sin eso el asesor lee «Sí, envíenmela» como si el cliente lo
  // hubiera escrito, y una plantilla como un texto libre del bot.
  // ⭐ T-CAL-12 · I-173 · la nota de voz DEL BOT (no la del asesor) lleva debajo lo
  // que dijo y su costo; su `cuerpo.texto` va en ese bloque y NO repetido como
  // texto suelto. Y el texto de respaldo de una nota que Meta rechazó (I-167,
  // `cuerpo.respaldo_de`) se rotula: sin eso el asesor no sabe por qué el bot
  // escribió lo que iba a decir en voz.
  const notaDelBot = !entrante && esBot && m.tipo === "audio";
  const respaldo = m.cuerpo?.respaldo_de;
  const esRespaldo = !entrante && m.tipo === "texto"
    && ((typeof respaldo === "string" && respaldo !== "") || typeof respaldo === "number");
  const tocoBoton = entrante && m.cuerpo?.boton === true;
  const esPlantilla = !entrante && m.tipo === "plantilla";
  const botonesPlantilla = esPlantilla && Array.isArray(m.cuerpo?.botones)
    ? (m.cuerpo.botones as unknown[]).filter((b): b is string => typeof b === "string")
    : [];

  return (
    <div className={`flex ${entrante ? "justify-start" : "justify-end"} px-3 sm:px-5`}>
      <div
        className={`burbuja ${entrante ? "burbuja-in" : "burbuja-out"} ${nuevo ? "entra-burbuja" : ""} max-w-[85%] sm:max-w-[68%] md:max-w-[60%]
          ${fondo} rounded-2xl ${entrante ? "rounded-tl-sm" : "rounded-tr-sm"} px-3 py-2 my-1 shadow-[var(--shadow-tarjeta)]`}
        style={{ ["--cola" as any]: cola }}
      >
        {!entrante && (
          <div className="flex items-center gap-1 mb-0.5">
            {esBot
              ? <><Bot size={11} className="text-marca" /><span className="text-[10.5px] font-semibold text-acento uppercase tracking-wider">Bot</span></>
              : <><User size={11} className="opacity-50" /><span className="text-[10.5px] font-semibold text-tinta-2 uppercase tracking-wider">Asesor</span></>}
            {esPlantilla && (
              <span className="text-[10.5px] font-semibold text-tinta-2 uppercase tracking-wider">· plantilla</span>
            )}
          </div>
        )}
        {esRespaldo && (
          <div className="flex items-center gap-1 mb-0.5" data-respaldo-de-voz>
            <Mic size={10} className="opacity-60" />
            <span className="text-[10.5px] text-tinta-2">Enviado en texto porque la nota de voz no salió</span>
          </div>
        )}
        {tocoBoton && (
          <div className="flex items-center gap-1 mb-0.5">
            <span className="text-[10.5px] font-semibold text-tinta-2 uppercase tracking-wider">tocó un botón</span>
          </div>
        )}
        {url && (
          <img src={url} alt="Imagen enviada al cliente" loading="lazy"
            className="rounded-lg mb-1.5 max-h-64 w-auto object-cover" />
        )}
        {/* ⛔ R-ADJ-01 · LA RAMA SE DECIDE POR QUIÉN MANDÓ EL MENSAJE, JAMÁS POR SI
            HAY FILA DE MEDIA. Decía `m.media` / `!m.media`, y funcionaba solo
            mientras los salientes NO tenían fila. Desde la 0045 la tienen todos, así
            que con la condición vieja un PDF o un video del asesor se pintaba como
            burbuja VACÍA (`MediaDelCliente` solo dibuja audio e imagen) y su foto o
            su nota de voz salían con el rótulo «que envió el cliente». Arreglar el
            envío no puede romper la pantalla. */}
        {/* C2-D4: lo que mandó el cliente (su voz, su foto). Antes: burbuja vacía. */}
        {entrante && m.media && <MediaDelCliente m={m} firmar={firmarMedia} />}
        {/* C2-D6: lo que adjuntó el ASESOR. Sin esto caería en el MISMO agujero
            que los entrantes —burbuja muda— pero del lado de salida, que es peor:
            el asesor no sabría si el archivo se mandó. */}
        {!entrante && typeof m.cuerpo?.storage_path === "string" && (
          <AdjuntoSaliente m={m} firmar={firmarMedia} />
        )}
        {notaDelBot && <LoQueDijoElBot m={m} />}
        {notaDelBot && <RastroDeLaNota m={m} respondio={respondio} />}
        {m.cuerpo?.texto && !notaDelBot && (
          // El inbox muestra el MISMO formato que ve el cliente en su WhatsApp
          // (*negrita*, _cursiva_…). Verlo crudo impedía juzgar lo que dice el bot.
          <p className="text-[14.5px] leading-[1.45] whitespace-pre-wrap break-words">
            {formatoWhatsApp(String(m.cuerpo.texto))}
          </p>
        )}
        {/* Los botones de la plantilla: fichas de SOLO LECTURA (son de WhatsApp
            del cliente, no de este panel). */}
        {botonesPlantilla.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1" aria-label="Botones de la plantilla">
            {botonesPlantilla.map((b, i) => <Chip key={i} tono="neutro">{b}</Chip>)}
          </div>
        )}
        <div className="flex items-center justify-end gap-1 mt-0.5 text-[10.5px] text-tinta-2 cifra">
          {new Date(m.creado_en).toLocaleTimeString("es-CO", { hour: "numeric", minute: "2-digit" })}
          {!entrante && <Entrega estado={m.entrega} />}
        </div>
      </div>
    </div>
  );
}


/**
 * Adjuntar un archivo y grabar una nota de voz (C2-D6).
 *
 * ⚠️ EL FORMATO DE LA GRABACIÓN IMPORTA. El navegador graba en lo que puede, y
 * WhatsApp solo reproduce como *nota de voz* unos pocos formatos. Si se graba en
 * `webm` —lo único que soporta Chrome en algunas versiones— el mensaje igual
 * llega, pero como ARCHIVO ADJUNTO: el cliente ve un documento en vez de poder
 * darle play. Por eso se prefiere `mp4`/`ogg` y, si toca caer en webm, se le
 * DICE al asesor en vez de dejarlo creer que mandó una nota de voz.
 */
const FORMATOS_GRABACION = [
  { mime: "audio/mp4", ext: "m4a", nota: true },       // Safari y Chrome reciente
  { mime: "audio/ogg;codecs=opus", ext: "ogg", nota: true },
  { mime: "audio/webm;codecs=opus", ext: "webm", nota: false }, // llega como archivo
];

function eligeFormatoGrabacion(): { mime: string; ext: string; nota: boolean } | null {
  const MR = (globalThis as any).MediaRecorder;
  if (!MR?.isTypeSupported) return null;
  for (const f of FORMATOS_GRABACION) if (MR.isTypeSupported(f.mime)) return f;
  return null;
}

/**
 * Tope de la grabación. WhatsApp aguanta mucho más, pero el límite real es el
 * bucket (50 MB) y, sobre todo, la paciencia del cliente: nadie oye una nota de
 * 9 minutos. Al llegar aquí se PARA Y SE ENVÍA, nunca se descarta: tirar cinco
 * minutos de voz de un asesor sería el peor final posible.
 */
const MAX_SEG_GRABACION = 300;
/** Cuántas barras tiene el medidor de voz. */
const BARRAS_NIVEL = 16;

function mmss(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

interface EstadoGrabacion {
  grabando: boolean;
  segundos: number;
  /** Historial reciente de volumen (0..1) para dibujar la onda. Vacío = sin medidor. */
  niveles: number[];
  empezar: () => Promise<void>;
  /** `true` = mandar la nota; `false` = tirarla a la basura. */
  terminar: (enviar: boolean) => void;
}

/**
 * La grabadora de notas de voz (C2-D6, pulido del 1-ago-2026, noche).
 *
 * Por qué es un hook y no dos botones sueltos: mientras el micrófono está
 * abierto, la fila de redacción entera cambia de estado y hay que verlo. La
 * versión anterior solo cambiaba el icono a un cuadrado y mostraba un chip
 * que —por estar `absolute` sin ancestro posicionado— se dibujaba fuera de la
 * pantalla: en la práctica **el asesor no tenía ninguna señal de que estaba
 * grabando**, y el cuadrado no decía si iba a enviar o a descartar.
 *
 * Lo que devuelve alimenta esa barra: el reloj (¿cuánto llevo?), la onda de voz
 * (¿me está oyendo el micrófono?) y dos salidas EXPLÍCITAS.
 */
function useGrabadora(
  mandar: (archivo: File) => Promise<void>,
  avisar: (t: string | null) => void,
): EstadoGrabacion {
  const [grabando, setGrabando] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [niveles, setNiveles] = useState<number[]>([]);
  const sesion = useRef<{
    mr: any; stream: any; audio?: any; reloj?: any; raf?: number;
    trozos: Blob[]; enviar: boolean; fmt: { mime: string; ext: string; nota: boolean };
  } | null>(null);

  // Soltar SIEMPRE lo que se tomó prestado: el micrófono queda encendido (con su
  // punto rojo en la pestaña) hasta que se cierra el stream, y un AudioContext
  // vivo por cada grabación termina agotando el cupo del navegador.
  const soltar = () => {
    const s = sesion.current;
    if (!s) return;
    clearInterval(s.reloj);
    if (s.raf) cancelAnimationFrame(s.raf);
    try { s.stream?.getTracks?.().forEach((t: any) => t.stop()); } catch { /* ya cerrado */ }
    try { s.audio?.close?.(); } catch { /* ya cerrado */ }
  };

  // Desmontar el hilo con el micrófono abierto (cambiar de conversación, cerrar
  // sesión) tiene que apagarlo igual.
  useEffect(() => () => soltar(), []);

  const empezar = async () => {
    const fmt = eligeFormatoGrabacion();
    if (!fmt) { avisar("Este navegador no puede grabar audio."); return; }
    avisar(null);
    let stream: any;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      avisar("No diste permiso para usar el micrófono.");
      return;
    }
    const mr = new (globalThis as any).MediaRecorder(stream, { mimeType: fmt.mime });
    const s = { mr, stream, trozos: [] as Blob[], enviar: true, fmt } as NonNullable<typeof sesion.current>;
    sesion.current = s;

    mr.ondataavailable = (e: any) => { if (e.data?.size) s.trozos.push(e.data); };
    mr.onstop = async () => {
      soltar();
      sesion.current = null;
      if (!s.enviar) return;                       // descartada: ni se arma el archivo
      const tipo = fmt.mime.split(";")[0];
      const blob = new Blob(s.trozos, { type: tipo });
      // Un toque accidental al micrófono no manda un archivo de 300 bytes que el
      // cliente recibe como un pitido.
      if (blob.size < 800) { avisar("La nota de voz quedó muy corta: no se envió."); return; }
      if (!fmt.nota) {
        avisar("Tu navegador graba en un formato que WhatsApp no reproduce: llegará como archivo adjunto.");
      }
      await mandar(new File([blob], `nota-de-voz.${fmt.ext}`, { type: tipo }));
    };
    mr.start();
    setGrabando(true);
    setSegundos(0);
    setNiveles([]);

    // El reloj. Se cuenta con el tiempo real, no sumando ticks: una pestaña en
    // segundo plano frena los timers y el contador mentiría hacia abajo.
    const inicio = Date.now();
    s.reloj = setInterval(() => {
      const t = Math.floor((Date.now() - inicio) / 1000);
      setSegundos(t);
      if (t >= MAX_SEG_GRABACION) {
        avisar(`Se alcanzó el máximo de ${MAX_SEG_GRABACION / 60} minutos: la nota se envió.`);
        s.enviar = true;
        try { mr.stop(); } catch { /* ya parada */ }
        setGrabando(false);
      }
    }, 250);

    // La onda de voz. No es adorno: es la única forma de que el asesor sepa que
    // el micrófono LO ESTÁ OYENDO. Un micrófono silenciado por el sistema graba
    // igual —el contador corre— y la nota sale muda; con la onda plana se ve
    // antes de mandarla.
    const AC = (globalThis as any).AudioContext ?? (globalThis as any).webkitAudioContext;
    if (AC) {
      try {
        const audio = new AC();
        s.audio = audio;
        const analizador = audio.createAnalyser();
        analizador.fftSize = 256;
        audio.createMediaStreamSource(stream).connect(analizador);
        const datos = new Uint8Array(analizador.frequencyBinCount);
        let ultimo = 0;
        const mirar = () => {
          if (sesion.current !== s) return;         // ya se paró
          s.raf = requestAnimationFrame(mirar);
          const ahora = Date.now();
          if (ahora - ultimo < 90) return;          // ~11 muestras/s: suficiente y barato
          ultimo = ahora;
          analizador.getByteTimeDomainData(datos);
          let suma = 0;
          for (const v of datos) { const d = (v - 128) / 128; suma += d * d; }
          const rms = Math.sqrt(suma / datos.length);
          // La voz normal vive en RMS ~0,02-0,25: sin este escalado la onda
          // apenas se movería y parecería que el micrófono no capta.
          const nivel = Math.min(1, rms * 4.5);
          setNiveles((prev) => [...prev, nivel].slice(-BARRAS_NIVEL));
        };
        s.raf = requestAnimationFrame(mirar);
      } catch { /* sin medidor: el contador y el punto rojo siguen bastando */ }
    }
  };

  const terminar = (enviar: boolean) => {
    const s = sesion.current;
    setGrabando(false);
    if (!s) return;
    s.enviar = enviar;
    if (!enviar) avisar("Nota de voz descartada.");
    try { s.mr.stop(); } catch { soltar(); sesion.current = null; }
  };

  return { grabando, segundos, niveles, empezar, terminar };
}

/** La onda de voz en vivo. Se dibuja de izquierda a derecha, como se habló. */
function OndaVoz({ niveles }: { niveles: number[] }) {
  if (niveles.length === 0) return null;
  const faltan = BARRAS_NIVEL - niveles.length;
  return (
    <div className="flex items-center gap-[3px] h-6 flex-1 min-w-0 px-1" aria-hidden="true">
      {Array.from({ length: BARRAS_NIVEL }).map((_, i) => {
        const n = i < faltan ? 0 : niveles[i - faltan];
        return (
          <span key={i}
            className="flex-1 min-w-[2px] max-w-[4px] rounded-full bg-peligro/70 transition-[height] duration-100"
            style={{ height: `${Math.max(3, n * 24)}px` }} />
        );
      })}
    </div>
  );
}

/**
 * La barra que reemplaza a la caja de texto mientras el micrófono está abierto.
 *
 * ⭐ Las DOS salidas son botones con NOMBRE, no un cuadrado ambiguo. El pedido
 * exacto de Cristhian (1-ago-2026): «el stop no dice si envía o descarta».
 */
function BarraGrabacion({ g }: { g: EstadoGrabacion }) {
  const cerca = g.segundos >= MAX_SEG_GRABACION - 30;
  return (
    <div role="status" aria-live="polite"
      className="entra flex items-center gap-2 rounded-xl border border-peligro/30 bg-peligro/[0.06] px-2.5 py-2">
      <span className="punto-grabando h-2.5 w-2.5 rounded-full bg-peligro shrink-0" aria-hidden="true" />
      <span className="text-[13px] font-semibold text-peligro cifra shrink-0">
        <span className="sr-only">Grabando nota de voz, </span>{mmss(g.segundos)}
      </span>
      <OndaVoz niveles={g.niveles} />
      {cerca && (
        <span className="hidden sm:inline text-[11px] text-tinta-2 shrink-0">
          máx. {MAX_SEG_GRABACION / 60} min
        </span>
      )}
      <div className="ml-auto flex items-center gap-1.5 shrink-0">
        <Boton tam="sm" tono="fantasma" onClick={() => g.terminar(false)}
          aria-label="Descartar la nota de voz">
          <Trash2 size={15} /><span className="hidden sm:inline">Descartar</span>
        </Boton>
        <Boton tam="sm" tono="marca" onClick={() => g.terminar(true)}
          aria-label="Enviar la nota de voz">
          <Send size={15} /><span className="hidden sm:inline">Enviar</span>
        </Boton>
      </div>
    </div>
  );
}

/**
 * ⭐ FLUIDEZ (2-nov-2026) · LA LISTA DE BURBUJAS VIVE APARTE Y MEMORIZADA.
 *
 * 🔴 LO QUE CIERRA: cada tecla en la caja de texto, cada décima de la grabadora y
 * cada palomita que llegaba en vivo volvían a pintar TODAS las burbujas del hilo.
 * Medido con `tools/banco-fluidez.mjs` (300 mensajes, CPU ×4): una ráfaga de
 * palomitas dejaba cuadros de 317 ms — la pantalla se trababa a la vista.
 * Con la lista y cada burbuja en `memo`, solo se repinta la burbuja que cambió.
 * ⚠️ Eso exige que el anfitrión cambie SOLO la fila tocada (`copia[i] = {...}`,
 * como ya hacen Inbox y PanelContacto) y que `firmarMedia` sea estable (useCallback).
 *
 * ⭐ Y LA ANIMACIÓN DE ENTRADA ES SOLO PARA LO NUEVO. Antes las 300 burbujas
 * nacían transparentes y subían a la vez al abrir un chat: el destello que se
 * veía «sobreexpuesto». Ahora lo que ya estaba al abrir aparece quieto, y solo
 * el mensaje que llega con el chat abierto entra con movimiento. El listón se
 * fija por conversación (la de las filas, no la del prop, porque al cambiar de
 * chat las filas viejas pueden seguir un instante en pantalla).
 */
const ListaMensajes = memo(function ListaMensajes({ mensajes, firmarMedia }: {
  mensajes: Mensaje[]; firmarMedia?: (ruta: string) => Promise<string | null>;
}) {
  const liston = useRef<{ conv: string; hasta: number } | null>(null);
  const conv = mensajes[0]?.conversacion_id;
  if (conv && liston.current?.conv !== conv) {
    liston.current = { conv, hasta: mensajes.reduce((mx, m) => (m.id > mx ? m.id : mx), 0) };
  }
  // Sin filas (chat vacío o cargando) no hay listón: nada se anima todavía.
  const hasta = conv && liston.current?.conv === conv ? liston.current.hasta : Infinity;
  return (
    <>
      {mensajes.map((m, i) => {
        const dia = diaDe(m);
        const diaAnterior = i > 0 ? diaDe(mensajes[i - 1]) : null;
        return (
          <div key={m.id} className="fila-hilo">
            {dia !== diaAnterior && (
              <div className="flex justify-center my-3">
                {/* Sólida y no esmerilada: el desenfoque se recalculaba en cada cuadro del scroll. */}
                <span className="microlabel bg-superficie px-2.5 py-1 rounded-full border border-borde-suave">
                  {new Date(m.creado_en).toLocaleDateString("es-CO", DIA_LARGO)}
                </span>
              </div>
            )}
            <BurbujaMemo m={m} firmarMedia={firmarMedia} nuevo={m.id > hasta}
              respondio={m.direccion === "out" && m.autor === "bot" && m.tipo === "audio" && respondioALaNota(mensajes, i)} />
          </div>
        );
      })}
    </>
  );
});
const BurbujaMemo = memo(Burbuja);

/** El día (hora local) de cada mensaje, calculado UNA vez por fila: cada repintado
 *  del hilo pedía 600 `new Date()` para decidir dónde van los separadores. La fila
 *  es inmutable (`mensajes` es append-only y el anfitrión copia solo la que cambia),
 *  así que la caché por objeto no puede quedar vieja. */
const DIAS = new WeakMap<Mensaje, string>();
function diaDe(m: Mensaje): string {
  let d = DIAS.get(m);
  if (d === undefined) { d = new Date(m.creado_en).toDateString(); DIAS.set(m, d); }
  return d;
}

export interface HiloProps {
  conversacion: Conversacion | null;
  mensajes: Mensaje[];
  cargando?: boolean;
  respuestas?: RespuestaRapida[];
  onEnviar: (texto: string) => Promise<void>;
  /**
   * El bot la tiene (`estado === "bot"`): transferir Y quedarse con ella.
   * ⚠️ OPCIONAL a propósito: sin saber QUIÉN toma el chat no se puede apuntar
   * dueño, así que el anfitrión lo pasa solo cuando hay usuario. Así `Hilo`
   * sigue sin enterarse de identidades y el botón no se dibuja solo.
   */
  onTomar?: () => void;
  /**
   * La escaló el BOT: ya está en `humano` y sin dueño. Mismo texto de botón que
   * `onTomar` —para el asesor es la misma acción— pero por dentro SOLO asigna.
   * Opcional por la misma razón que `onTomar`.
   */
  onReclamar?: () => void;
  onDevolver: () => Promise<void>;
  onCerrar: () => Promise<void>;
  /**
   * ⭐ T-HIB-1 · R-HIB-01 · darle una INSTRUCCIÓN al bot sin tomar el chat: el
   * cliente no la ve y el bot le responde enseguida siguiéndola (migr 0066).
   * ⛔ Es OTRA caja, no la del cliente: lo que se escribe aquí jamás se le manda
   * tal cual. Opcional: sin ella no se dibuja el botón.
   */
  onInstruir?: (texto: string) => Promise<void>;
  /**
   * ⭐ T-HIB-1 · fiscal H6 · si el chat lo tiene OTRA persona, el anfitrión no
   * pasa `onInstruir` (la base lo rechazaría) y pasa aquí el aviso que se muestra
   * en lugar del botón. `Hilo` sigue sin saber de identidades.
   */
  avisoInstruccion?: string;
  /**
   * C2-D4: pide una URL temporal para oír/ver un archivo del cliente. Se INYECTA
   * en vez de importarla porque `packages/ui` no habla con Supabase directamente
   * (C7-D1): la misma librería se monta en el panel y dentro del ERP.
   * Sin ella la burbuja muestra la transcripción, pero no el archivo.
   */
  firmarMedia?: (ruta: string) => Promise<string | null>;
  /** C2-D6: sube el archivo y lo manda al cliente. Sin esto no salen los botones. */
  onAdjuntar?: (archivo: File) => Promise<void>;
  /** Solo móvil: volver a la lista (el inbox se vuelve una columna). */
  onVolver?: () => void;
  /** Solo móvil: abrir la ficha 360 del contacto. */
  onVerFicha?: () => void;
  /** T-LAT-7 (R-LAT-06): un botón que pone AQUÍ quien monta el hilo, en la
   *  cabecera. Es una ranura y no un `onPlegar`+`plegado` a propósito: el estado
   *  de la ficha es del PANEL (que es quien la monta y quien la recuerda entre
   *  sesiones), y el hilo no tiene por qué saber nada de eso. */
  accionFicha?: ReactNode;
}

export function Hilo({
  conversacion: conv, mensajes, cargando, respuestas = [],
  onEnviar, onTomar, onReclamar, onDevolver, onCerrar, onVolver, onVerFicha, firmarMedia, onAdjuntar,
  accionFicha, onInstruir, avisoInstruccion,
}: HiloProps) {
  const [texto, setTexto] = useState("");
  // ⭐ T-HIB-1 · la caja de la instrucción al bot: su PROPIO estado, aparte del
  // mensaje al cliente, para que nada de lo escrito aquí pueda salir por allá.
  const [instruyendo, setInstruyendo] = useState(false);
  const [instruccion, setInstruccion] = useState("");
  const [enviandoInstr, setEnviandoInstr] = useState(false);
  const [errorInstr, setErrorInstr] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const finRef = useRef<HTMLDivElement>(null);
  const cajaRef = useRef<HTMLTextAreaElement>(null);

  const abierta = ventanaAbierta(conv?.ventana_expira_en);
  const cerrada = conv?.estado === "cerrada";
  const puedeEscribir = !!conv && !cerrada && abierta;

  // ── Adjuntos y nota de voz (C2-D6) ──────────────────────────────────────────
  // El estado vive aquí, y no dentro de un componente de botones, porque
  // mientras se graba la caja de texto DESAPARECE: es un modo de la fila
  // entera, no el estado de un botón.
  const inputArchivo = useRef<HTMLInputElement>(null);
  const [subiendo, setSubiendo] = useState<"voz" | "archivo" | null>(null);

  // 🔴 EL AVISO VA INDEXADO POR CONVERSACIÓN, y eso arregla un FALLO MUDO.
  // Con una sola cadena solo se podía elegir entre dos males: pintarlo sobre el
  // chat equivocado, o descartarlo. Descartarlo era peor — el asesor adjunta en
  // A, se cambia a B, la base rechaza: el mensaje NO se crea, el archivo queda
  // huérfano en el bucket, y **nadie se entera nunca**. Este repo lleva cinco
  // autopsias de fallos mudos; no se añade el sexto por ahorrar un objeto.
  // Guardado por conversación: no aparece donde no pasó, y sigue ahí cuando el
  // asesor vuelve a donde sí pasó.
  const [avisosAdj, setAvisosAdj] = useState<Record<string, string>>({});
  const avisoAdj = avisosAdj[conv?.id ?? ""] ?? null;
  const ponerAvisoDe = (id: string | undefined, texto: string | null) =>
    setAvisosAdj((prev) => {
      const clave = id ?? "";
      if (texto === null) { const copia = { ...prev }; delete copia[clave]; return copia; }
      return { ...prev, [clave]: texto };
    });
  // El de la conversación abierta AHORA: es el que usan la grabadora y el botón
  // de cerrar el aviso.
  const setAvisoAdj = (texto: string | null) => ponerAvisoDe(conv?.id, texto);

  const mandarArchivo = async (archivo: File, clase: "voz" | "archivo") => {
    if (!onAdjuntar) return;
    // 🔴 EL ID DE CUANDO EMPEZÓ LA SUBIDA, y hay que capturarlo aquí: para cuando
    // vuelva el `await`, el asesor puede estar en otra conversación. Los DOS
    // órdenes tienen que quedar bien: falla → cambio de chat, y cambio de chat →
    // falla. Con el aviso indexado por conversación los dos salen solos.
    // ⚠️ El daño del orden inverso era concreto: con la ventana de A vencida, en
    // B se leería «pasaron más de 24 horas» sobre un chat con la ventana ABIERTA,
    // y el asesor dejaría de responderle a un cliente que sí podía recibir.
    const idAlEmpezar = conv?.id;
    setSubiendo(clase); ponerAvisoDe(idAlEmpezar, null);
    try {
      await onAdjuntar(archivo);
    } catch (e: any) {
      // ⚠️ Contra `idAlEmpezar`, NUNCA contra la conversación abierta ahora.
      // El error viaja CRUDO desde `Inbox.tsx:284-293` (ese callback no lo
      // traduce), así que aquí es donde hay que traducirlo: sin `explicarError`
      // el asesor leería «VX_RUTA_AJENA: la ruta no corresponde a esta
      // conversación» tal cual.
      // ⚠️ El `?:` en vez de `explicarError(e) || …`: `explicarError` NUNCA
      // devuelve vacío (cae a «Algo salió mal.»), así que un `||` dejaría una
      // rama INALCANZABLE — y aquí el texto propio dice más que el genérico.
      ponerAvisoDe(idAlEmpezar,
        (e?.message || e?.code) ? explicarError(e) : "No se pudo enviar el archivo");
    } finally {
      setSubiendo(null);
    }
  };

  const grabadora = useGrabadora(
    (archivo) => mandarArchivo(archivo, "voz"),
    setAvisoAdj,
  );

  // Al llegar mensajes nuevos, bajar al final. ⭐ Al ABRIR un chat se salta
  // directo al final (sin deslizar): un chat corto antes se «paseaba» hasta
  // abajo cada vez que se abría y se sentía lento. Deslizar queda solo para el
  // mensaje que llega con el chat ya abierto.
  const ultimoChat = useRef<string | undefined>(undefined);
  useEffect(() => {
    const abriendo = ultimoChat.current !== conv?.id;
    ultimoChat.current = conv?.id;
    finRef.current?.scrollIntoView({ behavior: abriendo || mensajes.length > 40 ? "auto" : "smooth", block: "end" });
  }, [mensajes.length, conv?.id]);

  // ⛔ AQUÍ **NO** SE LIMPIA `avisosAdj`, y es a propósito. El banner del footer no
  // puede sobrevivir al cambio de chat (diría «no corresponde a ESTA conversación»
  // sobre otra donde nadie intentó nada), pero eso ya lo garantiza la INDEXACIÓN
  // por conversación — sin borrar nada. Borrarlo aquí volvería a perder el aviso
  // del chat que sí falló, que es el fallo mudo que se acabó de cerrar.
  useEffect(() => {
    setTexto(""); setError(null);
    setInstruyendo(false); setInstruccion(""); setErrorInstr(null);
  }, [conv?.id]);

  // Respuestas rápidas: se escriben "/atajo" y se sugieren mientras se teclea.
  const sugeridas = useMemo(() => {
    if (!texto.startsWith("/")) return [];
    const q = texto.slice(1).toLowerCase();
    return respuestas.filter((r) => r.atajo.toLowerCase().startsWith(q)).slice(0, 4);
  }, [texto, respuestas]);

  async function enviar() {
    const t = texto.trim();
    if (!t || enviando) return;
    setEnviando(true); setError(null);
    try {
      await onEnviar(t);
      setTexto("");
      cajaRef.current?.focus();
    } catch (e: any) {
      setError(e?.mensaje ?? e?.message ?? "No se pudo enviar.");
    } finally {
      setEnviando(false);
    }
  }

  async function instruir() {
    const t = instruccion.trim();
    if (!t || enviandoInstr || !onInstruir) return;
    setEnviandoInstr(true); setErrorInstr(null);
    try {
      await onInstruir(t);
      setInstruccion(""); setInstruyendo(false);
    } catch (e: any) {
      setErrorInstr(e?.mensaje ?? explicarError(e));
    } finally {
      setEnviandoInstr(false);
    }
  }

  if (!conv) {
    return (
      <div className="h-full lienzo grid place-items-center">
        <Vacio icono={<Send size={40} strokeWidth={1.25} />} titulo="Ninguna conversación abierta">
          Elige un chat de la lista para verlo aquí.
        </Vacio>
      </div>
    );
  }

  const nombre = nombreMostrado(conv.contacto);

  return (
    <section className="h-full flex flex-col min-w-0" aria-label={`Conversación con ${nombre}`}>
      {/* Cabecera SÓLIDA (2-nov-2026): el hilo no pasa por debajo —la cabecera es
          `shrink-0` fuera del scroll—, así que el esmerilado solo costaba sin verse.
          `CENTRO` mantiene una medida de lectura sana también en 1440p y 4K:
          sin tope, en 3840 px las burbujas se estiran y el texto se vuelve ilegible. */}
      <header className="shrink-0 flex items-center gap-2.5 px-3 sm:px-4 py-2.5 border-b border-borde-suave
        bg-superficie z-10">
        {onVolver && (
          <button onClick={onVolver} aria-label="Volver a la lista"
            className="lg:hidden p-1.5 -ml-1 rounded-md text-tinta-2 hover:text-tinta active:scale-90 transition-transform cursor-pointer">
            <ArrowLeft size={20} strokeWidth={1.75} />
          </button>
        )}
        <button onClick={onVerFicha} className="flex items-center gap-2.5 min-w-0 text-left cursor-pointer group">
          <Avatar nombre={nombre} size={38} />
          <span className="min-w-0">
            <span className="block font-semibold text-[15px] truncate group-hover:text-acento transition-colors">{nombre}</span>
            <span className="block text-[12px] text-tinta-2 truncate cifra">
              {esContactoIg(conv.contacto?.wa_id) && (
                <span data-canal="instagram" className="mr-1.5 px-1.5 py-px rounded bg-acento/10 text-acento font-semibold">Instagram</span>
              )}
              {identificadorVisible(conv.contacto)}
            </span>
          </span>
        </button>

        {/* En 320 px el nombre manda: el reloj de la ventana se baja a la barra
            de acciones, que sí tiene aire. */}
        <div className="ml-auto flex items-center gap-1.5">
          {conv.estado === "bot" && <Chip tono="marca" titulo="El bot está atendiendo"><Bot size={11} /> Bot</Chip>}
          {/* 🔴 «humano» NO significa que alguien la esté atendiendo: el bot
              escala a `humano` y deja `asignada_a` en NULL. Pintar las dos
              iguales hacía que el asesor pasara de largo creyendo que un
              compañero ya la tenía. */}
          {/* ⭐ T-LAT-7: la regla se REÚSA de `tipos.ts`, no se reescribe. El mismo
              rótulo vive en el inbox, aquí y en la tarjeta del embudo, y llegó a
              decidirse con dos reglas distintas a la vez (H-1, 2-sep-2026). */}
          {nadieLaHaTomado(conv.estado, conv.asignada_a) && (
            <Chip tono="peligro" titulo="El bot la pasó a un humano y todavía no la ha tomado nadie">
              <User size={11} /> Esperando asesor
            </Chip>
          )}
          {laTieneAlguien(conv.estado, conv.asignada_a) && (
            <Chip tono="ok" titulo="Un asesor tomó la conversación"><User size={11} /> Asesor</Chip>
          )}
          {cerrada && <Chip tono="apagado">Cerrada</Chip>}
          <span className="hidden sm:inline-flex">
            <Chip tono={abierta ? "neutro" : "alerta"} titulo="Ventana de 24 h de WhatsApp">
              <Clock size={11} /> {abierta ? restanteVentana(conv.ventana_expira_en) : "cerrada"}
            </Chip>
          </span>
          {accionFicha}
        </div>
      </header>

      {/* Acciones de la conversación (bot ⇄ humano) */}
      <div className="shrink-0 flex items-center gap-2 px-3 sm:px-4 py-2 border-b border-borde-suave bg-superficie overflow-x-auto scroll-fino">
        {conv.estado === "bot" && !cerrada && onTomar && (
          <Boton tam="sm" tono="marca" onClick={() => onTomar()}>Tomar el chat</Boton>
        )}
        {/* 🔴 Sin esto, una conversación que el BOT escaló no se podía tomar
            desde el panel NUNCA: el único botón miraba `estado === "bot"`. */}
        {conv.estado === "humano" && !conv.asignada_a && !cerrada && onReclamar && (
          <Boton tam="sm" tono="marca" onClick={() => onReclamar()}>Tomar el chat</Boton>
        )}
        {conv.estado === "humano" && !cerrada && (
          <Boton tam="sm" tono="neutro" onClick={() => onDevolver()}>Devolver al bot</Boton>
        )}
        {onInstruir && puedeEscribir && (
          <Boton tam="sm" tono={instruyendo ? "marca" : "neutro"} aria-expanded={instruyendo}
            onClick={() => setInstruyendo((v) => !v)}>
            <EyeOff size={13} /> Instrucción al bot
          </Boton>
        )}
        {!onInstruir && avisoInstruccion && puedeEscribir && (
          <span data-aviso-instruccion className="shrink-0">
            <Chip tono="apagado" titulo={avisoInstruccion}><EyeOff size={11} /> {avisoInstruccion}</Chip>
          </span>
        )}
        {!cerrada && <Boton tam="sm" tono="fantasma" onClick={() => onCerrar()}>Cerrar conversación</Boton>}
        {cerrada && <span className="text-[13px] text-tinta-2">Se reabre sola cuando el cliente vuelva a escribir.</span>}
        {!cerrada && (
          <span className="sm:hidden ml-auto shrink-0">
            <Chip tono={abierta ? "neutro" : "alerta"} titulo="Ventana de 24 h de WhatsApp">
              <Clock size={11} /> {abierta ? restanteVentana(conv.ventana_expira_en) : "cerrada"}
            </Chip>
          </span>
        )}
      </div>

      {/* Mensajes */}
      <div className="flex-1 overflow-y-auto scroll-fino lienzo py-3" role="log" aria-live="polite">
        <div className="max-w-[980px] mx-auto w-full">
        {cargando && <div className="text-center text-sm text-tinta-2 py-6">Cargando…</div>}
        {!cargando && mensajes.length === 0 && (
          <Vacio titulo="Sin mensajes todavía">Aquí aparecerá la conversación.</Vacio>
        )}
        <ListaMensajes mensajes={mensajes} firmarMedia={firmarMedia} />
        <div ref={finRef} />
        </div>
      </div>

      {/* Redacción */}
      <footer className="shrink-0 border-t border-borde-suave bg-superficie px-3 sm:px-4 py-2.5
        [&>*]:max-w-[980px] [&>*]:mx-auto space-y-2">
        {error && <Aviso tono="peligro" onCerrar={() => setError(null)}>{error}</Aviso>}

        {!abierta && !cerrada && (
          <Aviso tono="alerta">
            Pasaron 24 h desde el último mensaje del cliente. WhatsApp solo permite retomar con una
            <strong> plantilla aprobada</strong> (llegan en la etapa de campañas).
          </Aviso>
        )}

        {sugeridas.length > 0 && (
          <div className="flex gap-1.5 flex-wrap">
            {sugeridas.map((r) => (
              <button key={r.id} onClick={() => { setTexto(r.texto); cajaRef.current?.focus(); }}
                className="text-left text-[12.5px] px-2.5 py-1.5 rounded-lg border border-borde bg-elevado
                  hover:border-marca/40 active:scale-[0.98] transition cursor-pointer max-w-full">
                <span className="font-semibold text-acento">/{r.atajo}</span>
                <span className="text-tinta-2 ml-1.5 line-clamp-1">{r.texto}</span>
              </button>
            ))}
          </div>
        )}

        {avisoAdj && <Aviso tono="alerta" onCerrar={() => setAvisoAdj(null)}>{avisoAdj}</Aviso>}

        {/* ⭐ T-HIB-1 · LA CAJA DE LA INSTRUCCIÓN AL BOT — aparte de la del cliente. */}
        {onInstruir && instruyendo && puedeEscribir && (
          <div className="rounded-xl border border-dashed border-marca/50 bg-marca-suave/30 p-2.5 space-y-1.5"
            role="group" aria-label="Instrucción al bot">
            {errorInstr && <Aviso tono="peligro" onCerrar={() => setErrorInstr(null)}>{errorInstr}</Aviso>}
            <div className="flex items-end gap-2">
              <textarea
                value={instruccion}
                onChange={(e) => setInstruccion(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void instruir(); } }}
                rows={2}
                maxLength={1000}
                placeholder="Ej.: Ofrécele el Eco con envío gratis y sigue tú"
                aria-label="Instrucción para el bot"
                className="flex-1 resize-none max-h-32 min-h-[44px] px-3 py-2 rounded-lg bg-elevado border border-borde
                  text-[14px] leading-snug placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50 scroll-fino"
              />
              <Boton tono="marca" onClick={() => void instruir()}
                disabled={!instruccion.trim() || enviandoInstr}
                className="h-11 shrink-0 rounded-lg" aria-label="Enviar instrucción al bot">
                <Bot size={15} /> Enviar al bot
              </Boton>
            </div>
            <p className="text-[11.5px] text-tinta-2 flex items-center gap-1">
              <EyeOff size={10} /> El cliente no la ve. El bot le responde enseguida con sus palabras y sigue atendiendo.
            </p>
          </div>
        )}

        {/* Subiendo. Antes esto era un chip que —por estar `absolute` sin ancestro
            posicionado— se pintaba fuera de la pantalla: el asesor soltaba el
            archivo y no pasaba nada visible durante varios segundos. */}
        {subiendo && (
          <div className="flex items-center gap-2.5" role="status" aria-live="polite">
            <span className="h-1 flex-1 rounded-full progreso-indef" />
            <span className="text-[11.5px] text-tinta-2 shrink-0">
              Enviando {subiendo === "voz" ? "la nota de voz" : "el archivo"}…
            </span>
          </div>
        )}

        {onAdjuntar && (
          <input
            ref={inputArchivo} type="file" className="hidden" accept={FORMATOS_ADJUNTO}
            tabIndex={-1} aria-hidden="true"
            onChange={(e) => {
              const f = e.target.files?.[0];
              // Se limpia el input SIEMPRE: sin esto, elegir el MISMO archivo dos
              // veces seguidas no dispara el evento y parece que el botón no sirve.
              e.target.value = "";
              if (f) void mandarArchivo(f, "archivo");
            }}
          />
        )}

        {grabadora.grabando ? (
          <BarraGrabacion g={grabadora} />
        ) : (
          <div className="flex items-end gap-2">
            <textarea
              ref={cajaRef}
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); enviar(); } }}
              rows={1}
              disabled={!puedeEscribir}
              placeholder={
                cerrada ? "Conversación cerrada"
                  : !abierta ? "Fuera de la ventana de 24 h"
                  : "Escribe un mensaje…"
              }
              aria-label="Mensaje para el cliente"
              className="flex-1 resize-none max-h-32 min-h-[44px] px-3.5 py-3 rounded-xl bg-elevado border border-borde
                text-[15px] leading-snug placeholder:text-tinta-2/70 focus:outline-none focus:border-marca/50
                disabled:opacity-50 scroll-fino"
              style={{ height: "auto" }}
            />
            {onAdjuntar && (<>
              <Boton tono="fantasma" onClick={() => inputArchivo.current?.click()}
                disabled={!puedeEscribir || subiendo !== null}
                className="h-11 w-11 !px-0 rounded-xl" aria-label="Adjuntar archivo"
                title="Foto, PDF, Word o Excel">
                <Paperclip size={17} />
              </Boton>
              <Boton tono="fantasma" onClick={() => void grabadora.empezar()}
                disabled={!puedeEscribir || subiendo !== null}
                className="h-11 w-11 !px-0 rounded-xl" aria-label="Grabar nota de voz"
                title="Grabar una nota de voz">
                <Mic size={17} />
              </Boton>
            </>)}
            <Boton tono="marca" onClick={enviar} disabled={!puedeEscribir || !texto.trim() || enviando}
              className="h-11 w-11 !px-0 rounded-xl" aria-label="Enviar mensaje">
              <Send size={17} />
            </Boton>
          </div>
        )}
        <p className="text-[11px] text-tinta-2 hidden sm:flex items-center gap-1">
          <Zap size={10} />
          {conv.estado === "bot" && !cerrada
            ? "Al enviar tomas el chat: el bot deja de responder."
            : <>El mensaje queda <strong className="mx-1">encolado</strong> hasta que el worker lo entregue a WhatsApp.</>}
        </p>
      </footer>
    </section>
  );
}
